import React from 'react'
import ReactDOMServer from 'react-dom/server'
import PropTypes from 'prop-types'
import NextImage from 'next/legacy/image'
import { API_IMAGE_OBJECT } from '../../utils/types'
import { imgArea } from '../../utils/image'
import { ClientOnly } from '../SSRSwitch'
import styles from './styles'
import psPlaceholder from '../../assets/images/ps-placeholder.png'
import stvPlaceholder from '../../assets/images/stv-placeholder.png'

const getAspectRatioPercentage = (a = '16x9') => {
  const parts = a.split('x')
  return (parts[1] / parts[0]) * 100
}

const Image = ({
  image, sportsExclusive, alt, sizes, priority, width, height, className,
}) => {
  const placeholder = sportsExclusive ? psPlaceholder : stvPlaceholder

  // Process image data to get src, srcSet and aspectRatio
  const getImageParams = (imageParam) => {
    if (!imageParam || !imageParam._renditions) {
      return {
        src: placeholder,
        srcSet: placeholder,
      }
    }

    const renditions = imageParam._renditions
    const sortedBySize = sortBySize(renditions)
    const [largestSize] = sortedBySize
    const src = renditions[largestSize]
    const srcSet = sortedBySize
      .map(dim => `${renditions[dim]} ${dim.split('x')[0]}w`)
      .join(', ')
    const aspectRatio = largestSize

    return { src, srcSet, aspectRatio }
  }
  const { src, srcSet, aspectRatio } = getImageParams(image)
  const aspectRatioPercentage = getAspectRatioPercentage(aspectRatio)

  const sizeProps = {
    layout: 'fill',
  }
  const exactSizeProps = {
    layout: 'responsive',
    width,
    height,
  }

  // Check if URL has query parameters to determine if we need unoptimized
  const hasQueryParams = src && src.includes('?')

  const imgTag = (
    // these 2 divs are a workaroud for a bug with next/image filling console with warnings
    <div style={{ position: 'absolute', width: '100%', height: '100%' }}>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        {src
        && (
          <NextImage
            alt={alt}
            src={src}
            className="fade-in next-image"
            placeholder="blur"
            blurDataURL={placeholder}
            priority={priority}
            sizes={sizes}
            unoptimized={hasQueryParams} // Use unoptimized for URLs with query parameters
            style={{ borderRadius: '8px' }}
            {...(width && height ? exactSizeProps : sizeProps)}
          />
        )}
      </div>
    </div>
  )

  const scriptlessImgTag = (
    <img
      alt={alt}
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      width="350px"
      height="350px"
      className="fade-in"
      style={{ borderRadius: '8px' }}
    />
  )

  return (
    <div className={`Image ar-${aspectRatio} ${className}`}>
      <noscript
        dangerouslySetInnerHTML={{ // eslint-disable-line
          __html: ReactDOMServer.renderToStaticMarkup(scriptlessImgTag),
        }}
      />
      <ClientOnly>
        {imgTag}
      </ClientOnly>
      <style jsx>{styles}</style>
      <style jsx>{`
        .ar-${aspectRatio} {
          padding-bottom: ${aspectRatioPercentage}%;
          position: relative;
        }
      `}
      </style>
    </div>
  )
}

function sortBySize(renditions) {
  return Object.keys(renditions)
    .sort((a, b) => imgArea(b) - imgArea(a))
}

Image.propTypes = {
  image: API_IMAGE_OBJECT,
  alt: PropTypes.string,
  sizes: PropTypes.string,
  priority: PropTypes.bool,
  height: PropTypes.number,
  width: PropTypes.number,
  className: PropTypes.string,
  sportsExclusive: PropTypes.bool,
}

Image.defaultProps = {
  image: null,
  alt: '',
  sizes: '100vw',
  priority: false,
  height: null,
  width: null,
  className: '',
  sportsExclusive: false,
}

export default Image
